import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import {
    elementToEditorData,
    extractSuggestions,
    getUnresolvedChanges,
    hideInLineTrackTip,
    processContent,
} from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useDuplicateArticleValidation } from 'components/WorkspaceEditor/features/duplicateArticleValidation'
import {
    KeyHandler,
    KeyHandlerParams,
} from 'components/WorkspaceEditor/features/keyOperations/types'
import { createKeyHandler } from 'components/WorkspaceEditor/features/keyOperations/utils'
import {
    isLocalPromoteDemoteBlur,
    unsavedParagraphDraft,
    usePromoteDemote,
} from 'components/WorkspaceEditor/features/promoteDemote'
import { CONTENT_PLACEHOLDER_TEXT } from 'components/WorkspaceEditor/features/scrollToNewContent/constants'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { SetProjectContentItemElementsResponse } from 'components/WorkspaceEditor/types'
import {
    ADDITIONAL_ELEMENT_SUFFIX,
    getBaseElementId,
    replaceNodeElementsByElementId,
    withDebounce,
} from 'components/WorkspaceEditor/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { batchUpdate } from 'shared/utils/workspaceUtils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from '../../../../Workspace2Store'
import { useKeyOperation } from '../../../../features/keyOperations/hooks/useKeyOperation'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from '../../../../features/keyOperations/store/NodeMapStore'
import { useContentItemViewNode } from '../../../../hooks'
import type { ClassicEditorHandler } from '../../../ContentEditor'
import { detectContentChanges } from '../plainTextChangeDetection'
import { hasKeyChanged } from '../utils/keyChangeDetection'
import { useKeyChangeConfirmation } from './useKeyChangeConfirmation'

/**
 * Performance-optimized hook for initial data processing
 * Reduces expensive DOM parsing operations
 */
const useOptimizedInitialData = (textElement: TextContentElement) => {
    // Cache elementToEditorData with more specific dependencies
    const initialData = useMemo(() => {
        if (!textElement?.text) return ''
        return elementToEditorData(textElement)
    }, [textElement?.id, textElement?.text]) // Only recompute if ID or text changes

    // Cache suggestions more aggressively
    const initialSuggestions = useMemo(() => {
        if (!textElement?.id) return []
        return extractSuggestions([textElement])
    }, [textElement.id, textElement.changeType, textElement.authoredUserId])

    return { initialData, initialSuggestions }
}

interface UsePlainTextHandlersParams {
    textElement: TextContentElement
    contentItem: Partial<TextContentElement>
    unsavedData: string
    hasDeletedWholeText: boolean
    setUnsavedData: (value: React.SetStateAction<string>) => void
    setHasDeletedWholeText: (value: boolean) => void
    unFocusEditor: () => void
    handleAddContentExecuteOptimisticWithFallback: (
        unsavedData: string,
        textElement: TextContentElement,
        blurStartActiveTab?: number
    ) => Promise<void>
    handleContentMutationCompleted: (
        textElement: TextContentElement,
        isArticle: boolean,
        shouldSetTextContentElementToAddNull?: boolean,
        blurStartActiveTab?: number,
        mutationResponse?: SetProjectContentItemElementsResponse,
        originalElementsCount?: number,
        changedElementsCount?: number
    ) => Promise<void>
    prependKeyedTextElement: (elements: any[]) => void
    editorRef: any
    shouldTriggerNewContent: boolean
    setShouldTriggerNewContent: (value: boolean) => void
    startEditing: (contentId: string) => void
    registerNewEditor: (editor: any) => void
    registerEditorRef: (editor: any) => void
    ckeToggle: {
        activate: () => void
        deactivate: () => void
        active: boolean
    }
    setClickCoordinates: (coords: { x: number; y: number } | undefined) => void
    /** Whether this element is part of a split key (first element of keyed text without colon) */
    isSplitKeyElement?: boolean
    /** The full keyed text (e.g., "Label: ") for split key confirmation dialog */
    keyedText?: string
    /** Whether this element should be treated as keyed text (excludes BOD/MFR content) */
    isKeyedText: boolean
    /** Whether the key contains any base content (non-ADDED elements) */
    isBaseContentKey?: boolean
    /** Whether any key-portion element has an accepted tracked change (further key warnings are suppressed) */
    hasAcceptedKeyChanges?: boolean
    /** Whether the parent node contains keyed text (any contentItemNode has a colon) */
    nodeHasKeyedText?: boolean
    /** The full key text from node's contentItemNodes (e.g., "Samples for Initial Selection:") */
    nodeKeyedText?: string
    /** Whether the key was stripped from CKEditor and must be re-prepended before saving */
    shouldPrependKeyOnSave?: boolean
    /** Whether this element is after the key delimiter (colon) — value content, not key */
    isAfterKeyDelimiter?: boolean
    /** Ref to insertPillAtCursor — set after the callback is defined to avoid ordering issues */
    onInsertPillRef?: React.MutableRefObject<(() => void) | undefined>
    /** Ref to insertUOMAtCursor — enables the Ctrl+U shortcut in the OM workspace plain-text editor */
    onInsertUOMRef?: React.MutableRefObject<(() => void) | undefined>
    /** One-shot guard: when true, handleOnBlur skips the save (set by insertPillAtCursor to suppress blur on CKEditor unmount). */
    skipNextBlurRef?: React.MutableRefObject<boolean>
}

const CKEDITOR_ACTIVATION_DELAY = 100

/**
 * Custom hook that provides all event handlers for the plain text editor.
 * Consolidates blur, keydown, and focus handling logic.
 */
export const usePlainTextHandlers = ({
    textElement,
    contentItem,
    unsavedData,
    hasDeletedWholeText,
    setUnsavedData,
    setHasDeletedWholeText,
    unFocusEditor,
    handleAddContentExecuteOptimisticWithFallback,
    handleContentMutationCompleted,
    prependKeyedTextElement,
    editorRef,
    shouldTriggerNewContent,
    setShouldTriggerNewContent,
    startEditing,
    ckeToggle,
    setClickCoordinates,
    isSplitKeyElement,
    keyedText,
    isKeyedText,
    isBaseContentKey,
    hasAcceptedKeyChanges,
    nodeHasKeyedText,
    nodeKeyedText,
    shouldPrependKeyOnSave,
    isAfterKeyDelimiter,
    onInsertPillRef,
    onInsertUOMRef,
    skipNextBlurRef,
}: UsePlainTextHandlersParams) => {
    const chatUser = useChatProfileStore((state) => state.chatUser)
    const {
        isInProject,
        textContentElementToAdd,
        isEditingPlainText,
        setContentItemViewNodes,
        setTextContentElementToAdd,
        setIsEditingPlainText,
        contentItemViewNodes,
        treeRef,
        isTreeMappingInProgress,
        triggerBPSyncCheck,
    } = useShallowSelector((s) => ({
        isInProject: s.selectedProjectElementNode?.isInProject,
        textContentElementToAdd: s.textContentElementToAdd,
        isEditingPlainText: s.isEditingPlainText,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
        setIsEditingPlainText: s.actions.setIsEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        treeRef: s.treeRef,
        isTreeMappingInProgress: s.isTreeMappingInProgress,
        triggerBPSyncCheck: s.actions.triggerBPSyncCheck,
    }))

    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )
    // Ref to store mutation response for UPDATE operations
    const mutationResponseRef = useRef<any>(null)

    const { executeWithOptimistic } = useTransactionManager()
    const { mutateContentElements } = useContentMutationService()
    const { nodeToAdd, previousContentItemViewNodes } = useContentItemViewNode()
    const {
        register,
        handlePromoteDemoteShortcutKey,
        commitPendingPromoteDemote,
    } = usePromoteDemote()
    const { validateAdd, validateUpdate } = useDuplicateArticleValidation()
    const {
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
    } = useNodeMapActions()
    const { checkAndConfirmKeyChange } = useKeyChangeConfirmation()

    // Use optimized initial data processing
    const { initialData, initialSuggestions } = useOptimizedInitialData(
        textElement
    )
    // Use refs for stable references to reduce dependency array size
    const stableRefs = useRef({
        chatUserId: chatUser?.id,
        mutateContentElements,
        executeWithOptimistic,
        prependKeyedTextElement,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        validateAdd,
        validateUpdate,
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
        triggerBPSyncCheck,
        checkAndConfirmKeyChange,
    })

    // Update refs when dependencies change
    stableRefs.current = {
        chatUserId: chatUser?.id,
        mutateContentElements,
        executeWithOptimistic,
        prependKeyedTextElement,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        validateAdd,
        validateUpdate,
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
        triggerBPSyncCheck,
        checkAndConfirmKeyChange,
    }

    const handleOnBlur = useCallback<ClassicEditorHandler>(
        async (event, editor) => {
            // Not a user save — the row is being re-parented. Ctrl+S/Enter still pass.
            if (isLocalPromoteDemoteBlur(event)) return

            // Capture before the await below tears down this editor instance —
            // reading getData() after would return '' and empty the save payload.
            const editorHtml = editor.getData()

            // Move must land before the text save, since both touch contentItemViewNodes.
            await commitPendingPromoteDemote()

            // Prevent blur processing when browser window loses focus
            // to ensure consistent save behavior across different focus states
            if (!document.hasFocus()) {
                if (skipNextBlurRef?.current) skipNextBlurRef.current = false
                return
            }

            // Skip blur save when CKEditor is being unmounted by pill insertion
            if (skipNextBlurRef?.current) {
                skipNextBlurRef.current = false
                setIsEditingPlainText(false)
                ckeToggle.deactivate()
                return
            }

            // Quick check: if editor content matches original and there's no pending add,
            // skip save processing and just deactivate
            if (editorHtml === initialData && !textContentElementToAdd) {
                setIsEditingPlainText(false)
                ckeToggle.deactivate()
                return
            }

            const blurStartActiveTab = useWorkspace2Store.getState().activeTab
            const refs = stableRefs.current

            // Check if user is clicking on an action button with touchpad
            // Touchpad clicks trigger blur before the click event fires, causing the button
            // to be blocked by race condition protection. We detect action button mousedown
            // and skip save logic to allow the click to proceed.
            const workspaceState = useWorkspace2Store.getState()
            const isClickingActionButton =
                workspaceState.isActionButtonMousedown

            if (isClickingActionButton) {
                // Clean up marker and return early to allow the button click to fire
                workspaceState.actions.setIsActionButtonMousedown(false)
                return
            }

            const isSystemBusy =
                isSaveOperationInProgress || (isTreeMappingInProgress ?? false)
            const isElementBusy =
                refs.isElementBeingAdded(textElement.id) ||
                refs.isElementRecentlyAdded(textElement.id)

            // WORKAROUND: Block blur processing during race-prone operations
            if (isSystemBusy || isElementBusy) {
                return
            }

            const changeDetection = detectContentChanges({
                unsavedData,
                originalText: textElement.text,
                nodeToAdd,
                previousContentItemViewNodes,
                initialSuggestions,
                hasDeletedWholeText,
                textContentElementToAdd,
                isEditingPlainText,
            })

            const isBlurOnNewlyAddedEmptyContainer =
                changeDetection.shouldRevertToPrevious &&
                textContentElementToAdd?.id === textElement.id &&
                !unsavedData.trim()

            if (isBlurOnNewlyAddedEmptyContainer) {
                // Keep empty container visible - user must explicitly cancel (ESC) or type content
                return
            }

            if (changeDetection.shouldRevertToPrevious) {
                // Only revert if blur is on the element being added
                // Don't revert if blur is on a different element (e.g., old paragraph when new one was just added)
                const isBlurOnDifferentElement =
                    textContentElementToAdd?.id &&
                    textContentElementToAdd.id !== textElement.id

                if (isBlurOnDifferentElement) {
                    // Just deactivate the old editor, don't revert
                    setIsEditingPlainText(false)
                    ckeToggle.deactivate()
                    return
                }

                batchUpdate(() => {
                    setUnsavedData(textElement.text)
                    setTextContentElementToAdd(null)
                    setContentItemViewNodes(previousContentItemViewNodes || [])
                })
            }

            if (changeDetection.shouldDeactivate) {
                setIsEditingPlainText(false)
                return ckeToggle.deactivate()
            }
            if (changeDetection.shouldAddNewContent) {
                // Mark element immediately (synchronously) before async operation to prevent race conditions
                refs.markElementAsBeingAdded(textElement.id)

                if (
                    !(await refs.validateAdd(
                        unsavedData,
                        textElement,
                        editorRef
                    ))
                ) {
                    refs.markElementAsAdded(textElement.id)
                    return
                }

                refs.unFocusEditor()

                return await refs.handleAddContentExecuteOptimisticWithFallback(
                    unsavedData,
                    textElement,
                    blurStartActiveTab
                )
            }

            if (changeDetection.shouldExecuteOptimistic) {
                // Double-check race condition protection: Don't allow UPDATE operations during any concurrent save operations
                // This prevents content duplication when DELETE → ADD operations happen rapidly
                if (
                    isSaveOperationInProgress ||
                    refs.isElementBeingAdded(textElement.id)
                ) {
                    return
                }

                if (
                    !(await refs.validateUpdate(
                        unsavedData,
                        textElement,
                        editorRef
                    ))
                ) {
                    return
                }

                let changedElements = processContent(
                    editorHtml,
                    refs.chatUserId
                )

                // When isPTKey is true, EditorContent strips the key and gives
                // CKEditor only the value portion. Re-prepend the key so the save payload
                // and change detection include the full text (key + value).
                // True when: isKeyedText && isPTKey && keyedTextValue !== undefined && !isContentTeam.
                if (shouldPrependKeyOnSave) {
                    refs.prependKeyedTextElement(changedElements)
                }

                // Get the plain text from changedElements to compare with original
                // Exclude DELETED elements as they represent removed content that won't be in final text
                const newText = changedElements
                    .filter((el) => el.changeType !== 'DELETED')
                    .map((el) => el.text || '')
                    .join('')

                // For key-portion elements without a colon (split key or intermediate elements in a keyed text node),
                // any text change is effectively a key modification because we're editing part of the key.
                // Elements after the key delimiter are value content and should NOT trigger key warnings.
                const textHasChanged = textElement.text !== newText
                const isKeyElementWithoutColon =
                    !isAfterKeyDelimiter &&
                    (isSplitKeyElement || nodeHasKeyedText) &&
                    !textElement.text?.includes(':')
                const keyWasModified = isKeyElementWithoutColon
                    ? textHasChanged
                    : hasKeyChanged(textElement.text, newText)

                // Only show confirmation for base content keyed text (not user-added)
                // Check if original text is base content (changeType is not 'ADDED')
                const isBaseContent =
                    contentItem.changeType?.toUpperCase() !== 'ADDED'

                // If key was modified on base content keyed text, ask for user confirmation
                // Default isBaseContentKey to true (protective: show warning when unknown)
                // Elements after the key delimiter are value content — skip key warning
                if (
                    !isAfterKeyDelimiter &&
                    (isKeyedText || nodeHasKeyedText) &&
                    keyWasModified &&
                    (isBaseContentKey ?? true) &&
                    !(hasAcceptedKeyChanges ?? false)
                ) {
                    // Pass split key options when editing a key-portion element without a colon
                    // This includes split key elements AND intermediate elements in keyed text nodes
                    // Without this, checkAndConfirmKeyChange re-checks hasKeyChanged which returns false
                    // for elements without a colon (since extractKey returns '' for both texts)
                    // Use nodeKeyedText as fallback when the current element doesn't have its own keyedText
                    const effectiveKeyedText = keyedText || nodeKeyedText
                    const splitKeyOptions =
                        isKeyElementWithoutColon && effectiveKeyedText
                            ? {
                                  isSplitKeyElement: true,
                                  fullKeyedText: effectiveKeyedText,
                              }
                            : undefined

                    const isKeyChangeConfirmed = await refs.checkAndConfirmKeyChange(
                        textElement.text,
                        newText,
                        splitKeyOptions
                    )

                    if (!isKeyChangeConfirmed) {
                        // User cancelled the key modification, revert and deactivate.
                        // Don't call editor.setData() here — it triggers CKEditor's onChange
                        // which corrupts unsavedData/hasDeletedWholeText state and causes a
                        // spurious API call on the next blur. Instead, deactivate() will cause
                        // the editor to unmount (via conditional rendering), and if the user
                        // re-opens the editor it will remount with the original initialData.
                        try {
                            batchUpdate(() => {
                                setUnsavedData(textElement.text)
                                setIsEditingPlainText(false)
                            })
                            ckeToggle.deactivate()
                        } catch (error) {
                            setIsEditingPlainText(false)
                        }
                        return
                    }
                }

                // For keyed text where key was NOT modified, skip prepending key element
                // The store already has the key element and ContentItemInputBuilder merge will preserve it
                // This prevents key duplication when editing existing keyed text values

                const isNewElementAfterKeyedElement = textElement.id?.includes(
                    ADDITIONAL_ELEMENT_SUFFIX
                )
                const updatedTextElementId = getBaseElementId(
                    textElement.id
                ) as string

                // Read latest tree from store to avoid stale closure data
                // (e.g., designation saves via a separate mutation may have updated the tree)
                const latestContentItemViewNodes = useWorkspace2Store.getState()
                    .contentItemViewNodes

                let optimisticTreeViewNodes = replaceNodeElementsByElementId(
                    latestContentItemViewNodes,
                    updatedTextElementId,
                    changedElements,
                    isNewElementAfterKeyedElement
                )

                // When key is modified, remove the old _key element to prevent duplication
                // replaceNodeElementsByElementId only replaces the value element by exact ID
                // The key element has a different ID (baseId_key), so it must be manually removed
                // Without this, the old key would remain alongside the new key, causing duplication
                if (isKeyedText && keyWasModified && optimisticTreeViewNodes) {
                    const keyElementIdToRemove = `${updatedTextElementId}_key`
                    optimisticTreeViewNodes = optimisticTreeViewNodes.map(
                        (node) => {
                            if (
                                node.elements?.some(
                                    (el) => el.id === keyElementIdToRemove
                                )
                            ) {
                                return {
                                    ...node,
                                    elements: node.elements.filter(
                                        (el) => el.id !== keyElementIdToRemove
                                    ),
                                }
                            }
                            return node
                        }
                    )
                }

                // Check if changedElements contains keyed text (text with colon)
                // Keyed paragraph format: "KeyName: text content"
                // Examples: "Key: test paragraph", "Note: important info"
                const changedElementsHasKeyedText = changedElements.some(
                    (element) => element.text?.trim().includes(':')
                )

                // Check if updating a default paragraph to keyed paragraph
                // If so, we need to remove the default paragraph from UI after server creates new content
                const shouldRemoveDefaultParagraph =
                    textElement.text === CONTENT_PLACEHOLDER_TEXT &&
                    changedElementsHasKeyedText

                const shouldReplaceWholeElements = false

                refs.executeWithOptimistic({
                    fallbackState: initialData,
                    asyncAction: async () => {
                        const result = await refs.mutateContentElements(
                            updatedTextElementId,
                            changedElements,
                            shouldReplaceWholeElements,
                            isNewElementAfterKeyedElement,
                            blurStartActiveTab
                        )
                        // Store mutation response in ref for access in successCbFn
                        mutationResponseRef.current = result
                        return result
                    },
                    successCbFn: async () => {
                        // Calculate original elements count from the node containing this textElement
                        const nodeWithElement = contentItemViewNodes.find(
                            (node) =>
                                node.elements?.some(
                                    (el) => el.id === textElement.id
                                )
                        )
                        const originalElementsCount =
                            nodeWithElement?.elements?.length || 1

                        // UPDATE operations: Pass mutation response from ref
                        await refs.handleContentMutationCompleted(
                            textElement,
                            nodeToAdd?.type === 'Article',
                            shouldRemoveDefaultParagraph,
                            blurStartActiveTab,
                            mutationResponseRef.current,
                            originalElementsCount,
                            changedElements.length
                        )
                        // Clear ref after use
                        mutationResponseRef.current = null

                        // Trigger BP sync check after content save (mirrors DraggableItem.tsx behavior)
                        refs.triggerBPSyncCheck?.()
                    },
                    errorCbFn: () => {
                        // if the optimistic update fails, we want to reset nodeToAdd so the user can retry adding again
                        if (nodeToAdd) {
                            setTextContentElementToAdd(null)
                        }
                        setContentItemViewNodes(contentItemViewNodes)
                    },
                })

                batchUpdate(() => {
                    setUnsavedData('')
                    setContentItemViewNodes(optimisticTreeViewNodes)
                    setTextContentElementToAdd(null)
                    setIsEditingPlainText(false)
                    setHasDeletedWholeText(false)
                })
                ckeToggle.deactivate()
            }
        },
        [
            commitPendingPromoteDemote,
            isSaveOperationInProgress,
            isTreeMappingInProgress,
            unsavedData,
            textElement.text,
            textElement.id,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            textContentElementToAdd,
            isEditingPlainText,
            contentItemViewNodes,
            initialData,
            ckeToggle,
            setContentItemViewNodes,
            setTextContentElementToAdd,
            setIsEditingPlainText,
            isSplitKeyElement,
            keyedText,
            isKeyedText,
            isBaseContentKey,
            hasAcceptedKeyChanges,
            nodeHasKeyedText,
            nodeKeyedText,
            shouldPrependKeyOnSave,
            isAfterKeyDelimiter,
        ]
    )

    const {
        handleDeactivateAndAddContentPlainText,
        executeSaveAndAddContent,
        executeSaveOnly,
        executeEscapeKey,
        executeNodeNavigation,
    } = useKeyOperation({
        editorRef,
        contentItemId: contentItem.id,
        handleOnBlur,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        unFocusEditor,
        setUnsavedData,
        initialData,
    })

    // Store the last mouse position for cursor positioning
    const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null)

    // Store timer ID for click coordinate cleanup to prevent memory leaks
    const clickCoordinateTimerRef = useRef<NodeJS.Timeout | null>(null)

    /**
     * Handles mouse movement to update the last known cursor position
     * @param event - Mouse event containing the current cursor position
     */
    const handleOnMouseMove = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            requestAnimationFrame(() => {
                lastMousePositionRef.current = {
                    x: event.clientX,
                    y: event.clientY,
                }
            })
        },
        []
    )

    /**
     * Handles click events to store the click coordinates for cursor positioning
     * @param event - Mouse event containing the click coordinates
     */
    const handleOnClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (!ckeToggle.active) {
                // Store click coordinates for cursor positioning
                const { clientX: x, clientY: y } = event

                // Set coordinates for ContentEditor to use
                setClickCoordinates({ x, y })
                // Also update the ref for immediate use
                lastMousePositionRef.current = { x, y }
            }
        },
        [ckeToggle.active, setClickCoordinates]
    )

    // Reusable key handlers array - shared between keyDown and keyUp
    const keyHandlers: KeyHandler[] = useMemo(
        () => [
            // Escape key handler
            ({ event, textElementText, changeDetection }: KeyHandlerParams) =>
                executeEscapeKey(event, textElementText!, changeDetection!),

            // Save only handler (Ctrl+S)
            ({ event, changeDetection }: KeyHandlerParams) =>
                executeSaveOnly(event, changeDetection),

            // Deactivate and add content handler
            ({ event, changeDetection }: KeyHandlerParams) =>
                handleDeactivateAndAddContentPlainText(event, changeDetection!),

            // Save and add content handler (Enter)
            ({ event, changeDetection }: KeyHandlerParams) =>
                executeSaveAndAddContent(event, changeDetection),

            // Node navigation handler (Arrow keys)
            ({ event, treeRef }: KeyHandlerParams) =>
                executeNodeNavigation(event, treeRef),

            // Promote/demote handler
            ({ event }: KeyHandlerParams) => {
                handlePromoteDemoteShortcutKey(event)
                return false
            },

            // Insert pill shortcut (Ctrl+Alt+P) — OM workspace only
            ({ event }: KeyHandlerParams) => {
                if (
                    onInsertPillRef?.current &&
                    event.ctrlKey &&
                    event.altKey &&
                    event.key.toLowerCase() === 'p'
                ) {
                    event.preventDefault()
                    onInsertPillRef.current()
                    return true
                }
                return false
            },

            // Insert UOM shortcut (Ctrl+U) — OM workspace only
            ({ event }: KeyHandlerParams) => {
                if (
                    onInsertUOMRef?.current &&
                    event.ctrlKey &&
                    !event.altKey &&
                    !event.shiftKey &&
                    !event.metaKey &&
                    event.key.toLowerCase() === 'u'
                ) {
                    event.preventDefault()
                    onInsertUOMRef.current()
                    return true
                }
                return false
            },
        ],
        [
            executeEscapeKey,
            executeSaveOnly,
            handleDeactivateAndAddContentPlainText,
            executeSaveAndAddContent,
            executeNodeNavigation,
            handlePromoteDemoteShortcutKey,
        ]
    )

    const handleOnFocusCapture = useCallback(
        (event?: React.FocusEvent<HTMLElement>) => {
            // Check if focus is on a drawing designation ReplacementOption
            // If so, skip PlainText activation to allow ReplacementOption to handle it
            if (event?.target) {
                const target = event.target as HTMLElement

                const isDrawingDesignation = target.closest(
                    '[data-testid*="-drawing-designation"]'
                )
                if (isDrawingDesignation) {
                    return
                }
            }

            // Hide balloon panels immediately on focus
            hideInLineTrackTip()

            // Use the last known mouse position if coordinates aren't already set
            if (lastMousePositionRef.current && !ckeToggle.active) {
                setClickCoordinates(lastMousePositionRef.current)
            }

            setIsEditingPlainText(true)
            register(contentItem, isInProject, contentItemViewNodes)
            ckeToggle.activate()

            // Clear any existing timer to prevent memory leaks
            if (clickCoordinateTimerRef.current) {
                clearTimeout(clickCoordinateTimerRef.current)
            }

            // Clear click coordinates after CKEditor has time to use them
            clickCoordinateTimerRef.current = setTimeout(() => {
                setClickCoordinates(undefined)
                lastMousePositionRef.current = null
                clickCoordinateTimerRef.current = null
            }, CKEDITOR_ACTIVATION_DELAY)
        },
        [
            setIsEditingPlainText,
            register,
            contentItem,
            isInProject,
            contentItemViewNodes,
            ckeToggle,
            setClickCoordinates,
        ]
    )

    // KeyDown handler - for Enter, Save, and general keys
    const handleOnKeyDown = useCallback(
        createKeyHandler('down', {
            handlers: keyHandlers,
            detectContentChanges,
            unsavedData,
            originalText: textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        }),
        [
            keyHandlers,
            unsavedData,
            textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        ]
    )

    // KeyUp handler - for Escape key (more reliable)
    const handleOnKeyUp = useCallback(
        createKeyHandler('up', {
            handlers: keyHandlers,
            detectContentChanges,
            unsavedData,
            originalText: textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        }),
        [
            keyHandlers,
            unsavedData,
            textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        ]
    )

    const debouncedOnChange = useMemo(
        () =>
            withDebounce<ClassicEditorHandler>((_, editor) => {
                const hasInitialSuggestions = initialSuggestions.length > 0
                const editorHtml = editor.getData()

                const textChanges = getUnresolvedChanges(
                    editorHtml,
                    textElement.text,
                    hasInitialSuggestions
                )
                setUnsavedData(textChanges)

                // Survives the virtualized row unmounting (scroll away, re-parent).
                unsavedParagraphDraft.saveIfAdding(
                    textElement.id,
                    editorHtml,
                    textChanges
                )

                // Mark as deleted only if original had text and now it's empty
                // This prevents false positives when clicking on already empty text
                setHasDeletedWholeText(
                    Boolean(textElement.text?.trim()) && !textChanges.trim()
                )

                // for scroll-into-content: monitor temporary content when user starts typing
                startEditing(textElement.id)
            }, 0),
        [
            textElement.text,
            textElement.id,
            initialSuggestions,
            setUnsavedData,
            setHasDeletedWholeText,
            startEditing,
        ]
    )

    const processEditorContent = useCallback(
        (htmlContent: string) =>
            processContent(htmlContent, stableRefs.current.chatUserId),
        []
    )

    // side-effect that will prevent memory leaks from unloading CKEditor
    useEffect(() => {
        return () => debouncedOnChange.cancel()
    }, [debouncedOnChange])

    // Cancel pending change operations when browser window loses focus
    // to maintain consistent editor state during focus transitions
    useEffect(() => {
        const handleWindowBlur = () => {
            if (debouncedOnChange) {
                debouncedOnChange.cancel()
            }
        }

        window.addEventListener('blur', handleWindowBlur)
        return () => window.removeEventListener('blur', handleWindowBlur)
    }, [debouncedOnChange])

    // Cleanup timer on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (clickCoordinateTimerRef.current) {
                clearTimeout(clickCoordinateTimerRef.current)
            }
        }
    }, [])

    return {
        handleOnBlur,
        handleOnFocusCapture,
        handleOnClick,
        handleOnMouseMove,
        handleOnKeyDown,
        handleOnKeyUp,
        debouncedOnChange,
        initialSuggestions,
        processEditorContent,
    }
}
